import { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { InvestmentOpportunity } from '../../services/investment.service';
import { Loader } from '../Loader';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';

interface TopDealsProps {
  opportunities: InvestmentOpportunity[];
  loading: boolean;
}

export default function TopDeals({ 
  opportunities, 
  loading
}: TopDealsProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const { isIntersecting } = useIntersectionObserver({
    ref: containerRef,
    threshold: 0.1
  });

  // Виртуализация: показываем только первые 20 элементов, остальные загружаем при скролле
  const [visibleCount, setVisibleCount] = useState(20);
  const ITEMS_PER_LOAD = 10;

  // Мемоизируем сортировку
  const sortedDeals = useMemo(() => {
    return [...opportunities].sort((a, b) => Number(b.apy) - Number(a.apy));
  }, [opportunities]);

  // Виртуализация: загружаем больше элементов при скролле
  const visibleDeals = useMemo(() => {
    return sortedDeals.slice(0, visibleCount);
  }, [sortedDeals, visibleCount]);

  // Загружаем больше элементов когда контейнер виден
  useEffect(() => {
    if (isIntersecting && visibleCount < sortedDeals.length) {
      const timer = setTimeout(() => {
        setVisibleCount(prev => Math.min(prev + ITEMS_PER_LOAD, sortedDeals.length));
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isIntersecting, visibleCount, sortedDeals.length]);

  const handleDealClick = (opportunity: InvestmentOpportunity) => {
    navigate(`/asset/${opportunity.id}`);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    target.onerror = null;
    target.src = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"><text x="50%" y="50%" font-size="20" fill="%23999" text-anchor="middle" dy=".35em">${target.alt[0]}</text></svg>`;
  };

  if (loading) {
    return (
      <div className="p-5 text-center text-tg-hint">
        <Loader />
      </div>
    );
  }

  if (!opportunities.length) {
    return (
      <div className="p-5 text-center text-tg-error">
        {t('HOME_SCREEN.AVAILABLE_DEALS.NO_DEALS')}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex flex-col gap-12 w-full max-[360px]:gap-9">
      <div className="flex flex-col gap-2 relative max-[360px]:gap-1.5">
        {/* Разделитель для первой секции */}
        <div 
          className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-10 h-1 rounded-sm bg-tg-secondary-bg opacity-50 max-[360px]:-bottom-4.5 max-[360px]:w-8 max-[360px]:h-0.5"
          style={{ borderRadius: '2px' }}
        />
        
        {/* Заголовок секции - обычная секция (last-child) */}
        <div 
          className="flex justify-between items-start relative bg-tg-bg overflow-hidden"
          style={{
            padding: '20px',
            margin: '0 0 12px 0',
            borderRadius: '20px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), inset 0 0 0 1px rgba(255, 0, 128, 0.1)'
          }}
        >
          {/* Градиентный фон для обычной секции - section:last-child .section_header::before */}
          <div 
            className="absolute inset-0 z-[1] pointer-events-none"
            style={{
              borderRadius: '20px',
              background: `
                radial-gradient(circle at top left, rgba(255, 0, 128, 0.08), transparent 50%),
                radial-gradient(circle at bottom right, rgba(255, 107, 0, 0.08), transparent 50%)
              `,
              opacity: 0.5
            }}
          />
          
          {/* Вращающийся градиент - section:first-child .section_header::after */}
          <div 
            className="absolute pointer-events-none"
            style={{
              top: '-100%',
              left: '-100%',
              width: '300%',
              height: '300%',
              background: 'conic-gradient(from 0deg at 50% 50%, #FFD700 0deg, transparent 60deg, transparent 300deg, #FF69B4 360deg)',
              animation: isIntersecting ? 'rotate 8s linear infinite' : 'none',
              opacity: 0.1,
              zIndex: 0
            }}
          />
          
          <div className="flex flex-col relative z-[2]" style={{ gap: '8px' }}>
            <h2 
              className="m-0 relative z-[2]"
              style={{
                fontSize: '24px',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #ff0080, #FF6B00)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '-0.2px',
                color: 'transparent'
              }}
            >
              {t('HOME_SCREEN.AVAILABLE_DEALS.TITLE')}
            </h2>
            <p 
              className="text-tg-hint relative z-[2]"
              style={{ 
                fontSize: '14px',
                margin: '4px 0 0'
              }}
            >
              {t('HOME_SCREEN.AVAILABLE_DEALS.CAPTION')}
            </p>
          </div>
        </div>

        {/* Карточки с виртуализацией */}
        {visibleDeals.map(opportunity => (
          <div key={opportunity.id} className="relative" style={{ paddingTop: '6px' }}>
            <div 
              className={cn(
                'rounded-[20px] bg-tg-bg',
                'relative overflow-hidden transition-all duration-200 cursor-pointer',
                'active:scale-[0.98]'
              )}
              style={{
                padding: '16px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06), inset 0 0 0 1px var(--tg-theme-secondary-bg-color)'
              }}
              onClick={() => handleDealClick(opportunity)}
            >
              {/* Фоновый эффект card::before */}
              <div 
                className="absolute inset-0 z-[1] pointer-events-none"
                style={{
                  background: 'var(--tg-theme-secondary-bg-color)',
                  opacity: 0.5
                }}
              />
              
              <div className="flex items-center relative z-[2]" style={{ gap: '16px' }}>
                {/* Логотип */}
                <div className="flex-shrink-0">
                  <img 
                    src={opportunity.logo_url} 
                    alt={opportunity.platform} 
                    className="rounded-full object-contain"
                    style={{ width: '48px', height: '48px', padding: 0 }}
                    onError={handleImageError}
                  />
                </div>
                
                {/* Информация */}
                <div className="flex-grow flex flex-col" style={{ gap: '8px' }}>
                  {/* Заголовок и платформа */}
                  <div className="flex justify-between items-center">
                    <h3 className="font-semibold m-0 text-tg-text" style={{ fontSize: '17px' }}>
                      {opportunity.type}
                    </h3>
                    <span className="text-tg-hint" style={{ fontSize: '14px' }}>
                      {opportunity.platform}
                    </span>
                  </div>
                  
                  {/* Сеть и APY */}
                  <div className="flex justify-between items-center">
                    <div className="flex items-center" style={{ gap: '6px' }}>
                      <img 
                        src={opportunity.network_logo_url} 
                        alt={opportunity.network}
                        className="rounded-full"
                        style={{ width: '20px', height: '20px' }}
                        onError={handleImageError}
                      />
                      <span className="text-tg-hint" style={{ fontSize: '14px' }}>
                        {opportunity.network}
                      </span>
                    </div>
                    <div 
                      className="text-white rounded-xl font-bold relative z-[2]"
                      style={{
                        background: 'linear-gradient(135deg, #FF0080, #FF6B00)',
                        padding: '6px 12px',
                        borderRadius: '12px',
                        fontSize: '14px',
                        letterSpacing: '0.2px',
                        boxShadow: '0 4px 12px rgba(255, 0, 128, 0.2)'
                      }}
                    >
                      APY {opportunity.apy}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}